import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * MCD ULTRA v3.0 - Perfect Section Fit Wrapper
 * 
 * Enforces the "One Section = One Viewport" rule.
 * 
 * Architecture:
 * - Height: Strictly `var(--page-h)`.
 * - Scroll: Internal auto-scroll if content overflows.
 * - Snap: Always aligns to start.
 * 
 * Usage:
 * <SectionWrapper id="hero">
 *   <MyContent />
 * </SectionWrapper>
 */
const SectionWrapper = React.forwardRef(({ id, children, className, sx = [], mobileSafe = false, ...props }, ref) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const effectiveMobileSafe = mobileSafe || isMobile;

    return (
        <Box
            id={id}
            ref={ref}
            component="section"
            className={`section ${className || ''}`.trim()} // Merged classes
            {...props}
            sx={[
                {
                    // 1. Strict Dimensions
                    height: effectiveMobileSafe ? 'auto' : 'var(--page-h)', // Calculated as 100dvh - headerHeight
                    minHeight: effectiveMobileSafe ? 'auto' : 'initial',
                    width: '100%',

                    // 2. Scroll Snap Behavior
                    scrollSnapAlign: 'start',
                    scrollSnapStop: 'always', // Force stop at this section

                    // 3. Layout & Internal Scrolling
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: effectiveMobileSafe ? 'visible' : 'hidden', // Prevent leakage
                    position: 'relative'
                },
                ...(Array.isArray(sx) ? sx : [sx])
            ]}
        >
            {/* 
               Internal Content Container 
               Allows content to scroll INTERNALLY if it exceeds the fixed page height.
            */}
            <Box
                className="section-body"
                sx={{
                    flex: 1,
                    // Critical for nested flex/grid layouts inside fixed-height sections.
                    // Without this, children with default min-height can force overflow.
                    minHeight: 0,
                    overflowY: effectiveMobileSafe ? 'visible' : 'auto', // Enable internal scroll
                    overflowX: 'hidden',
                    // Prevent wheel/touch scroll from chaining to the outer .scroller.
                    // This stops the page from unexpectedly snapping to other sections/footer.
                    overscrollBehaviorY: effectiveMobileSafe ? 'auto' : 'contain',
                    WebkitOverflowScrolling: 'touch',
                    display: 'flex',
                    flexDirection: 'column',
                    // Scrollbar styling for a premium feel
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: '3px',
                    },
                    '&:hover::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.4)',
                    }
                }}
            >
                {children}
            </Box>
        </Box >
    );
});

// Verified Production Build
export default SectionWrapper;
